import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";

import "./i18n/i18n";
import { queryClient } from "./lib/query";
import { fetchRuntimeConfig, setRuntimeConfig } from "./lib/runtime-config";
import { AppRouterProvider } from "./router";

const env = (import.meta as ImportMeta & { env?: Record<string, string> }).env ?? {};
const mode = env.MODE ?? "development";
const sentryDsn = env.BUN_PUBLIC_SENTRY_DSN;

Sentry.init({
  dsn: sentryDsn,
  enabled: mode === "production" && !!sentryDsn,
  environment: mode,
});

const elem = document.getElementById("root")!;
async function loadRuntimeConfig() {
  try {
    const config = await fetchRuntimeConfig();
    setRuntimeConfig(config);
    queryClient.setQueryData(["runtime-config"], config);
  } catch {
    // If config fetch fails, fall back to in-memory defaults.
  }
}

await loadRuntimeConfig();

const app = (
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AppRouterProvider />
    </QueryClientProvider>
  </StrictMode>
);

if (import.meta.hot) {
  // With hot module reloading, `import.meta.hot.data` is persisted.
  const root = (import.meta.hot.data.root ??= createRoot(elem));
  root.render(app);
} else {
  // The hot module reloading API is not available in production.
  createRoot(elem).render(app);
}
